import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationDetails, NotificationHeader, NotificationTextMessage } from '@app/core/Enum';
import { CommonService } from '@app/core/Services';
import { HighlightRow } from '@app/core/Services/common/highlighted-texts.service';
import { CeaseFinancialYear, DeleteAccountingPeriod, LockFinancialYear } from '@app/core/Store';
import { Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { CleanAllLinesComponent } from '../clean-all-lines/clean-all-lines.component';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-add-financial-year',
  templateUrl: './add-financial-year.component.html',
  styleUrls: ['./add-financial-year.component.scss'],
})
export class AddFinancialYearComponent implements OnInit {
  formFinancialYearDetail: UntypedFormGroup;
  financialYearArray: any;
  isNewRow = false;
  notificationMessage = NotificationTextMessage;
  displayColumns: string[] = [
    'srNo',
    'startDate',
    'endDate',
    'deleteButton',
    'lockButton',
    'ceasedButton',
  ];
  financialYearData: any[] = [];
  tableDataSource: MatTableDataSource<AbstractControl>;
  today: any = moment();
  @Input() triggerEditData: Observable<any>;
  @Input() triggerEditFinancialData: Observable<any>;
  @Input() businessStartDate: any;
  minDate:Date=new Date();
  @Input() establishDate!: Date;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private highlightRow: HighlightRow,
    public datepipe: DatePipe,
    public commonService: CommonService,
    public store: Store,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.setJournalDetailsForm(true);
    this.triggerEditFinancialData?.subscribe((data) => {
      this.editFinancialYear(data);
    });

    this.formFinancialYearDetail.valueChanges.subscribe((value) => {
      this.commonService.isInitialValueChange =
        this.formFinancialYearDetail.touched;
    });
  }

  editFinancialYear(data: any): void {
    this.financialYearData = [];
    this.highlightRow.financialData = [];
    this.financialYearArray = this.formFinancialYearDetail.get(
      'financialYearArray'
    ) as UntypedFormArray;

    this.financialYearArray.clear();

    data.forEach((element) => {
      this.financialYearData?.push(element);
    });

    this.financialYearData.forEach((item, i) => {
      this.financialYearArray.push(this.buildOrderItemsForm(item));
      if (item.isLocked) {
        this.financialYearArray.controls[i].get('startDate').disable();
        this.financialYearArray.controls[i].get('endDate').disable();
      }
      if (item.isCeased) {
        this.financialYearArray.controls[i].get('startDate').disable();
        this.financialYearArray.controls[i].get('endDate').disable();
      }
      this.checkEndDate(this.financialYearArray.controls[i].get('endDate').value, i);
    });

    this.setDataSource(this.financialYearArray);
  }

  checkEndDate(endDate: any, i: any){
    if(!moment(endDate).isBefore(this.today)){
      // Use setValue to update the FormControl properly
      this.financialYearArray.controls[i].get('disableLockBtn')?.setValue(true);
    }
  }

  buildOrderItemsForm(item: any): FormGroup {
    this.highlightRow.financialData.push({
      id: item.id,
      startDate: this.datepipe.transform(item.fromDate, 'yyyy-MM-dd'),
      endDate: this.datepipe.transform(item.toDate, 'yyyy-MM-dd'),
      isLocked: item.isLocked,
    });

    return this.formBuilder.group({
      id: item.id,
      startDate: item.fromDate,
      endDate: item.toDate,
      attachment: '',
      minDate: item.fromDate,
      maxDate: item.toDate,
      isLocked: item.isLocked,
      disableLockBtn: item.disableLockBtn,
      isCeased: item.isCeased,
    });
  }

  setJournalDetailsForm(addNewRow: boolean): void {
    this.formFinancialYearDetail = new FormGroup({
      financialYearArray: new UntypedFormArray([]),
    });

    this.financialYearArray = this.formFinancialYearDetail.get(
      'financialYearArray'
    ) as UntypedFormArray;

    this.setDataSource(this.financialYearArray);
    if (addNewRow) this.createRow();
  }

  createRow(): void {
    this.financialYearArray = this.formFinancialYearDetail.get(
      'financialYearArray'
    ) as UntypedFormArray;

    if (this.formFinancialYearDetail.invalid) {
      this.commonService.onFailure(NotificationTextMessage.fillRequiredFields);
      return;
    }

    const lastIndex = this.financialYearArray.length - 1;

    if (lastIndex >= 0) {
      const lastEndDate = new Date(this.financialYearArray.controls[lastIndex].get('endDate')?.value);
      const nextStartDate = new Date(lastEndDate);
      nextStartDate.setDate(nextStartDate.getDate() + 1);

      this.financialYearArray.push(this.setForm(nextStartDate));
    } else {
      this.financialYearArray.push(this.setForm());
    }

    this.highlightRow.financialData = [];
    this.financialYearArray?.getRawValue().forEach((x) => {
      this.highlightRow.financialData.push({
        id: x.id,
        startDate: this.datepipe.transform(x.startDate, 'yyyy-MM-dd'),
        endDate: this.datepipe.transform(x.endDate, 'yyyy-MM-dd'),
        isLocked: x.isLocked,
      });
    });

    this.setDataSource(this.financialYearArray);
  }

  addNewRow(): void {
    this.isNewRow = true;
    for (let i = 0; i < 1; i++) {
      this.createRow();
    }
  }

  dateChange(isStartDate: boolean, index: number): void {
    const row = this.financialYearArray.controls[index];
    const startDate = new Date(row.get('startDate')?.value);
    const endDateCtrl = row.get('endDate');

    if (isStartDate) {
      const establishDate = new Date(this.establishDate);
      const startDateWithoutTime = new Date(startDate.setHours(0, 0, 0, 0));
      const establishDateWithoutTime = new Date(establishDate.setHours(0, 0, 0, 0));

      // Now compare the dates without the time
      if (startDateWithoutTime < establishDateWithoutTime) {
        // Set error on the startDate field
        row.get('startDate').setErrors({ invalidStartDate: true });

        // Show the notification message
        this.commonService.onFailure(NotificationTextMessage.startDateCannotBeBeforeEstablishDate);
      } else {
        // Clear any previous errors
        row.get('startDate').setErrors(null);
      }

      const previousEndDate = index > 0 ? new Date(this.financialYearArray.controls[index - 1].get('endDate').value) : null;

      if (previousEndDate) {
        const previousEndDateWithoutTime = new Date(previousEndDate);
        previousEndDateWithoutTime.setHours(0, 0, 0, 0);
        // Only proceed if the new startDate is after the previousEndDate
        if (startDateWithoutTime.getTime() <= previousEndDateWithoutTime.getTime()) {
          this.commonService.onFailure(NotificationTextMessage.startDateCanNotBeEarlier);
          row.get('startDate').setErrors({ invalidStartDate: true });
          return;
        } else {
          // Calculate the difference between start and previous end date
          const differenceInDays = (startDateWithoutTime.getTime() - previousEndDate.getTime()) / (1000 * 3600 * 24); // Get difference in days

          if (differenceInDays === 1) {
            // If difference is exactly one day, proceed with setting the min date and end date
            row.get('startDate').setErrors(null); // Clear any errors
            row.get('minDate')?.setValue(startDate);

            let defaultEndDate = new Date(startDate);
            defaultEndDate.setFullYear(defaultEndDate.getFullYear() + 1);
            defaultEndDate.setDate(defaultEndDate.getDate() - 1);
            endDateCtrl?.setValue(defaultEndDate);
          } else {
            let defaultEndDate = new Date(startDate);
            defaultEndDate.setFullYear(defaultEndDate.getFullYear() + 1);
            defaultEndDate.setDate(defaultEndDate.getDate() - 1);
            endDateCtrl?.setValue(defaultEndDate);
            // If the difference is more than 1 day, show an error message
            this.commonService.onFailure(NotificationTextMessage.startDateMustBeOneDayAfterPreviousEndDate);
            row.get('startDate').setErrors({ invalidStartDate: true });
          }
        }
      } else {
        let defaultEndDate = new Date(startDate);
        defaultEndDate.setFullYear(defaultEndDate.getFullYear() + 1);
        defaultEndDate.setDate(defaultEndDate.getDate() - 1);
        endDateCtrl?.setValue(defaultEndDate);
      }
    }

    const endDate = new Date(endDateCtrl?.value);
    const diffMonths =
      (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());

    if (diffMonths > 18 || (diffMonths === 18 && endDate.getDate() > startDate.getDate())) {
      endDateCtrl?.setErrors({ maxMonths: true });
      this.commonService.onFailure(NotificationTextMessage.moreThan18months);
    } else {
      if (endDateCtrl?.hasError('maxMonths')) {
        const errors = { ...endDateCtrl.errors };
        delete errors['maxMonths'];
        endDateCtrl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }

    this.highlightRow.financialData = [];
    this.financialYearArray?.getRawValue().forEach((x) => {
      this.highlightRow.financialData.push({
        id: x.id,
        startDate: this.datepipe.transform(x.startDate, 'yyyy-MM-dd'),
        endDate: this.datepipe.transform(x.endDate, 'yyyy-MM-dd'),
        isLocked: x.isLocked,
      });
    });

    this.checkEndDate(endDateCtrl?.value, index);
  }

  setDataSource(array: UntypedFormArray): void {
    this.tableDataSource = new MatTableDataSource(array.controls);
  }

  setForm(startDate?: Date): FormGroup {
    if (!startDate) {
      startDate = new Date();
    }

    let endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + 1);
    endDate.setDate(endDate.getDate() - 1);

    return this.formBuilder.group({
      id: new FormControl(Guid.EMPTY as unknown as Guid),
      startDate: new FormControl(startDate, Validators.required),
      endDate: new FormControl(endDate, Validators.required),
      attachment: new FormControl(),
      // initialize per-row min/max to the computed start and end dates
      minDate: new FormControl(startDate),
      maxDate: new FormControl(endDate),
      isLocked: new FormControl(false),
      disableLockBtn: new FormControl(true),
      isCeased: new FormControl(false),
    });
  }

  onDeleteProductDetails(index: number, element): void {
    if (this.financialYearArray.length > 1) {
      if (
        element.controls.id.value !== undefined &&
        element.controls.id.value !== null &&
        element.controls.id.value !== Guid.EMPTY
      ) {
        const param = {
          selectedData: this.financialYearArray.controls[index].value,
          isDelete: true,
          headerMessage: NotificationHeader.deleteConfirmation,
          message: NotificationDetails.deleteAllDetailText,
        };
        let ids: Guid[] = [];
        ids.push(element.controls.id.value);
        this.dialog
          .open(CleanAllLinesComponent, {
            data: param,
            disableClose: true,
          })
          .afterClosed()
          .subscribe((result) => {
            if (result) {
              this.spinner.show();
              this.store
                .dispatch(new DeleteAccountingPeriod(ids))
                .subscribe(() => {
                  this.financialYearArray = this.formFinancialYearDetail.get(
                    'financialYearArray'
                  ) as UntypedFormArray;
                  if (this.financialYearArray.length === 1) {
                    return;
                  }
                  this.financialYearArray.removeAt(index);
                  //this.highlightRow.financialData.splice(index, 1);
                  this.setDataSource(this.financialYearArray);
                  this.commonService.onSucess(NotificationTextMessage.successMessage);
                });
            }
          });
      } else {
        this.financialYearArray = this.formFinancialYearDetail.get(
          'financialYearArray'
        ) as UntypedFormArray;
        if (this.financialYearArray.length === 1) {
          return;
        }
        this.financialYearArray.removeAt(index);
       // this.highlightRow.financialData.splice(index, 1);
        this.setDataSource(this.financialYearArray);
      }
    } else {
      this.commonService.onFailure(
        'Minimum one accounting period is required!'
      );
    }
  }

  onLockClick(index: number, item: any): void {
    this.spinner.show();
    const list: any[] = [];

    const isCurrentlyLocked = this.financialYearArray.controls[index].get('isLocked').value;

    if (!isCurrentlyLocked) {
      this.financialYearArray.controls.forEach((ctrl, i) => {
        if (i <= index) {
          ctrl.get('isLocked').setValue(true);
          list.push(ctrl.value.id);
        }
      });
    } 
    else {
      this.financialYearArray.controls.forEach((ctrl, i) => {
        if (i >= index) {
          ctrl.get('isLocked').setValue(false);
          list.push(ctrl.value.id);
        }
      });
    }

    this.store.dispatch(new LockFinancialYear(list, !isCurrentlyLocked)).subscribe((res) => {
      this.spinner.hide();

      if (res !== undefined && res.company.isFinancialYearLocked) {
        this.commonService.onSucess(NotificationTextMessage.successMessage);

        this.financialYearArray = this.formFinancialYearDetail.get('financialYearArray') as UntypedFormArray;

        this.financialYearArray.controls.forEach((ctrl) => {
          const isLocked = ctrl.get('isLocked').value;
          const isCeased = ctrl.get('isCeased').value;

          if (isLocked || isCeased) {
            ctrl.get('startDate').disable();
            ctrl.get('endDate').disable();
          } else {
            ctrl.get('startDate').enable();
            ctrl.get('endDate').enable();
          }
        });
      } else {
        this.commonService.onFailure(NotificationTextMessage.errorMessage);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    
      if (changes['businessStartDate']) {
       if(this.businessStartDate){
        this.minDate=this.businessStartDate;
      
       }
       
      }
    }

    showBtnDelete(i: number): boolean {
      const id = this.financialYearArray.controls[i].get('id').value;

      const isLocked = this.financialYearArray.controls[i].get('isLocked').value;

      if (isLocked) return false;

      if (id === Guid.EMPTY) {
        return true;
      }

      return this.financialYearArray.length === (i + 1);
    }

    showBtnCeased(i: number): boolean {
      const id = this.financialYearArray.controls[i].get('id').value;

      if (id === Guid.EMPTY) {
        return true;
      }

      return this.financialYearArray.length === (i + 1);
    }

    showPeriod():boolean{
      
     return (this.financialYearArray.value).some((item:any)=>item.isCeased === true);
    }

    onCeaseClick(index: number, isCeased: boolean): void {
      this.spinner.show();
      this.store.dispatch(new CeaseFinancialYear(this.financialYearArray.controls[index].get('id').value, isCeased))
        .subscribe((res) => {
          if (res !== undefined && res.company.isFinancialYearCeased) {
            this.financialYearArray.controls[index].get('isCeased').setValue(isCeased);
            this.commonService.onSucess(NotificationTextMessage.successMessage);
            this.financialYearArray.controls.forEach((element, i) => {
              if (this.financialYearArray.controls[i].get('isCeased').value) {
                this.financialYearArray.controls[i].get('startDate').disable();
                this.financialYearArray.controls[i].get('endDate').disable();
              }else{
                if (this.financialYearArray.controls[i].get('isLocked').value) {
                  this.financialYearArray.controls[i].get('startDate').disable();
                  this.financialYearArray.controls[i].get('endDate').disable();
                } else {
                  this.financialYearArray.controls[i].get('startDate').enable();
                  this.financialYearArray.controls[i].get('endDate').enable();
                }
              }
            });
          } else {
            this.commonService.onFailure(NotificationTextMessage.errorMessage);
          }
        });
        
     
    }
  }
